(ns admin.components.forms.curriculum
  (:require [clojure.string :as str]
            [applied-science.js-interop :as j]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [reagent-form.core :as rf]
            [reagent-form.utils :as rfu]
            [reagent-form.validators :as v]
            [reagent-form.transformers :as t]

            [kyber.feature-flags :refer [feature-enabled?]]

            [kyber.components.base.button :as button]
            [kyber.components.base.text :as text]
            [kyber.components.base.icons :as icons]
            [kyber.components.base.link :as link]
            [kyber.styles.core :refer [gs styles->classes]]
            [kyber.lib.kyber-db :as kyber-db]

            [admin.router.utils :refer [admin-path]]
            [admin.events.curricula :as >curricula]
            [admin.components.forms.fields.toggle :as toggle]
            [admin.components.forms.fields.tag-collections :as tag-collections]
            [admin.components.forms.fields.clp-enabled :as clp-enabled-field]
            [admin.components.composite.resource-uploader :as resource-uploader]
            [admin.components.composite.crud-container :as crud-container]
            [kyber.constants :as c]))

(def matrix-header-height 30)

(def classes
  (styles->classes
   {:form-container
    {:display :flex
     :padding (gs [:spacing :p20])
     :background-color (gs [:colors :true-white])}

    :form-section-container
    {:justify-content :space-between
     :align-items :flex-start
     :padding-bottom (gs [:spacing :p32])
     :border-bottom (str "1px solid" (gs [:colors :gray-scale-100]))}

    :left-section
    {:width "50%"
     :margin-right (gs [:spacing :p20])}

    :right-section
    {:width "50%"
     :padding-left (gs [:spacing :p20])
     :border-left (str "1px solid" (gs [:colors :gray-scale-100]))}

    :admin-content-container
    {:display :flex
     :justify-content :space-between
     :align-items :center
     :padding (gs [:spacing :p8])
     :border-radius (gs [:radius :r7])
     :border (str "1px solid " (gs [:colors :error-500]))
     :background-color "rgba(255,0,0,0.1)"
     :margin-bottom (gs [:spacing :p20])}

    :truncate
    {:overflow :hidden
     :text-overflow :ellipsis
     :white-space :nowrap}

    :toggle-container
    {:display :flex
     :justify-content :space-between
     :align-items :center
     :margin-bottom (gs [:spacing :p20])}

    :admin-field-label
    {:color (gs [:colors :error-500])}

    :admin-input-container
    {:width "100px"
     :margin-left (gs [:spacing :p32])
     :padding-left (gs [:spacing :p20])
     :border-left (str "1px solid " (gs [:colors :gray-scale-50]))}

    :uploader-container
    {:margin-top (gs [:spacing :p20])
     :padding (gs [:spacing :p8])
     :border-radius (gs [:radius :r7])
     :margin-bottom (gs [:spacing :p20])
     :border (str "1px solid " (gs [:colors :gray-scale-200]))}

    :uploader-body
    {:width "100%"}

    :logo-uploader-container
    {:border-radius (gs [:radius :r7])
     :border (str "1px solid " (gs [:colors :error-500]))
     :background-color "rgba(255,0,0,0.1)"
     :padding (gs [:spacing :p8])}

    :image-wrapper
    {:display :flex
     :justify-content :center
     :align-items :center
     :width "100%"}

    :preview-image
    {:max-width "100%"}

    :matrix-container
    {:position :relative
     :display :flex
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-radius (gs [:radius :r3])
     :max-height "600px"
     :overflow-y :scroll}

    :matrix-container-left
    {:width "33.33333%"
     :height "100%"
     :box-shadow (gs [:shadows :shadow-12])}

    :matrix-container-right
    {:width "66.6666%"
     :height "100%"}

    :matrix-grid
    {:display :grid}

    :matrix-grid-cell
    {:border-bottom (str "1px solid " (gs [:colors :gray-scale-200]))
     :width "100%"}}))

(defn- pause-and-close
  [show-form]
  (js/setTimeout
   #(reset! show-form false)
   1000))

(defn- render-clp-poster-uploader
  [{curriculum-uuid :curriculum/uuid}]
  (let [upload-new (reagent/atom false)
        curriculum (re-frame/subscribe [::kyber-db/get-record {:model :curriculum
                                                               :ident curriculum-uuid}])]
    (fn [_]
      (let [{:curriculum/keys [clp-banner]} @curriculum]
        [:div {:class (:uploader-container classes)}
         [text/caption-30 :p "CLP Poster Image"]

         [text/caption-20 :p
          {:style {:margin-bottom (gs [:spacing :p12])}}
          "The CLP poster image is the banner at the top of the CLP page"]

         (if (true? @upload-new)
           [resource-uploader/render-inline-uploader
            {:on-success (fn [{:resource/keys [viewer-path]}]
                           (re-frame/dispatch
                            [::>curricula/update-curriculum-v2
                             {:curriculum/uuid curriculum-uuid
                              :curriculum/clp-banner viewer-path}
                             {:on-success #(pause-and-close upload-new)
                              :on-success-toast "CLP Poster Updated!"}]))
             :on-cancel #(reset! upload-new false)
             :public-access true
             :accepted-file-types #{:image}}]
           [:div
            (if (some? clp-banner)
              [:div {:class (:image-wrapper classes)}
               [:img {:key clp-banner
                      :src clp-banner
                      :class (:preview-image classes)}]]
              [text/title-10 :p "No file uploaded"])
            [button/primary-button
             {:style {:margin-top (gs [:spacing :p20])}
              :on-click #(do
                           (.preventDefault %)
                           (reset! upload-new true))}
             "Replace Image"]])]))))

(defn- render-curriculum-logo-uploader
  [{curriculum-uuid :curriculum/uuid}]
  (let [upload-new (reagent/atom false)
        curriculum (re-frame/subscribe [::kyber-db/get-record {:model :curriculum
                                                               :ident curriculum-uuid}])]
    (fn [_]
      (let [{:curriculum/keys [logo]} @curriculum]
        [:div {:class (:logo-uploader-container classes)}
         [text/caption-30 :p "Curriculum Logo"]

         [text/caption-20 :p
          {:style {:margin-bottom (gs [:spacing :p12])}}
          (str "The curriculum logo will default to the provider logo if none "
               "is provided")]

         (if (true? @upload-new)
           [resource-uploader/render-inline-uploader
            {:on-success (fn [{:resource/keys [viewer-path]}]
                           (re-frame/dispatch
                            [::>curricula/update-curriculum-v2
                             {:curriculum/uuid curriculum-uuid
                              :curriculum/logo viewer-path}
                             {:on-success-toast "Curriculum Logo Updated!"
                              :on-success #(pause-and-close upload-new)}]))
             :on-cancel #(reset! upload-new false)
             :public-access true
             :accepted-file-types #{:image}}]
           [:div
            (if (some? logo)
              [:div {:class (:image-wrapper classes)}
               [:img {:key logo
                      :src logo
                      :class (:preview-image classes)}]]
              [text/title-10 :p "No file uploaded"])
            [button/primary-button
             {:style {:margin-top (gs [:spacing :p20])}
              :on-click #(do
                           (.preventDefault %)
                           (reset! upload-new true))}
             "Replace Image"]])]))))

(defn- tag->tag-description [tag]
  (str "When enabled, this Curriculum will only be visible to Learners belonging to the following "
       tag
       "."))

(defn- tag->tag-label [tag]
  (str "Allowed " (->> tag
                       str/capitalize)))

(defn- tag-group-matrix
  [_]
  (let [right-col-ref! (atom nil)
        right-col-width (reagent/atom nil)]
    (reagent/create-class
     {:component-did-mount
      #(reset! right-col-width
               (j/get (j/call @right-col-ref! :getBoundingClientRect) :width))
      :reagent-render
      (fn [{:keys [curriculum-uuid activities curriculum-valid-tags]}]
        (let [activities*
              (map (fn [activity]
                     (assoc activity
                            :tag-uuids
                            (->> activity
                                 :activity/valid-tags
                                 (mapcat :content-provider.tag-group/values)
                                 (map :content-provider.tag/uuid)
                                 set)))
                   activities)]
          [:div {:class (:matrix-container classes)}
           ;; Tag groups & tags
           [:div {:class (:matrix-container-left classes)}
            [:div {:style {:padding-top (gs [:spacing :p12])
                           :padding-bottom (gs [:spacing :p2])}}
             (map-indexed
              (fn [idx
                   {tag-group-values :content-provider.tag-group/values
                    tag-group-name :content-provider.tag-group/name
                    :as tag-group}]
                (let [row-height
                      (+ 20 (when (zero? idx)
                              matrix-header-height))

                      grid-template-rows
                      (str row-height "px repeat(" (count tag-group-values) ",20px)")

                      ms-grid-rows
                      (clojure.string/join " " (into [(str row-height "px")]
                                                     (take (count tag-group-values)
                                                           (repeat "20px"))))]
                  ^{:key tag-group}
                  [:div {:class (:matrix-grid classes)
                         :style {:display :-ms-grid
                                 :grid-template-rows grid-template-rows
                                 :msGridRows ms-grid-rows
                                 :msGridColumns "1fr"
                                 :margin-bottom (gs [:spacing :p20])}}
                   [:div {:class (:truncate classes)
                          :style {:display :flex
                                  :flex-direction :column
                                  :justify-content :flex-end
                                  :msGridRow 1}}
                    [text/caption-30 :span {:style {:padding-left (gs [:spacing :p20])}}
                     tag-group-name]]
                   (map-indexed
                    (fn [idx {tag-name :content-provider.tag/name
                              :as tag}]
                      ^{:key tag}
                      [:div {:class [(:truncate classes)
                                     (:matrix-grid-cell classes)]
                             :style {:msGridRow (+ 2 idx)
                                     :padding-left (gs [:spacing :p20])
                                     :padding-right (gs [:spacing :p20])}}
                       [text/body-20 :span {:title tag-name} tag-name]])
                    tag-group-values)]))
              curriculum-valid-tags)]]

     ;; Activities and status
           (let [activity-count
                 (count activities*)

                 min-width 150

                 col-width
                 (if (<= (* activity-count min-width) @right-col-width)
                   "1fr"
                   (str min-width "px"))

                 grid-template-columns
                 (str "repeat(" activity-count "," col-width ")")

                 ms-grid-columns
                 (clojure.string/join " " (take activity-count (repeat col-width)))]
             [:div {:class (:matrix-container-right classes)
                    :style {:overflow-x :scroll}
                    :ref #(reset! right-col-ref! %)}
              [:div {:style {:padding-top (gs [:spacing :p12])
                             :padding-bottom (gs [:spacing :p2])}}
               [:div {:class (:matrix-grid classes)
                      :style {:display :-ms-grid
                              :grid-template-columns grid-template-columns
                              :msGridColumns ms-grid-columns}}
                (map-indexed
                 (fn [idx activity]
                   ^{:key activity}
                   [:div {:style {:width "100%"
                                  :height (str matrix-header-height "px")
                                  :min-width 0
                                  :padding-left (gs [:spacing :p4])
                                  :padding-right (gs [:spacing :p4])
                                  :text-align :center
                                  :msGridColumn (inc idx)}}
                    [text/body-20 :span {:class (:truncate classes)
                                         :style {:display :block}
                                         :title (:activity/name activity)}
                     (:activity/name activity)]
                    [link/link {:href (admin-path :activity-settings
                                                  :curriculum-uuid curriculum-uuid
                                                  :activity-uuid (:activity/uuid activity))}
                     "Edit Activity"]])
                 activities*)]
               (for [{tag-group-values :content-provider.tag-group/values
                      :as tag-group}
                     curriculum-valid-tags]
                 (let [grid-template-rows
                       (str "20px repeat(" (count tag-group-values) ",20px)")

                       ms-grid-rows
                       (clojure.string/join " " (take (count tag-group-values) (repeat "20px")))]
                   ^{:key tag-group}
                   [:div {:class (:matrix-grid classes)
                          :style {:margin-bottom (gs [:spacing :p20])
                                  :display :-ms-grid
                                  :grid-template-rows grid-template-rows
                                  :msGridRows ms-grid-rows
                                  :grid-template-columns grid-template-columns
                                  :msGridColumns ms-grid-columns}}
                    (map-indexed
                     (fn [idx _]
                       ^{:key idx}
                       [:div {:style {:text-align :center
                                      :msGridColumn (inc idx)
                                      :msGridRow 1}}])
                     activities*)

                    (map-indexed
                     (fn [row-idx
                          {tag-uuid :content-provider.tag/uuid
                           :as tag}]
                       ^{:key tag}
                       (map-indexed
                        (fn [col-idx activity]
                          (let [default-checked?
                                (nil? (some (set (map :content-provider.tag/uuid tag-group-values))
                                            (:tag-uuids activity)))]
                            ^{:key (str (:activity/uuid activity) tag-uuid)}
                            [:div {:class [(:truncate classes)
                                           (:matrix-grid-cell classes)]
                                   :style {:text-align :center
                                           :msGridColumn (inc col-idx)
                                           :msGridRow (+ 2 row-idx)}}
                             (when (or default-checked?
                                       (some? (get (:tag-uuids activity) tag-uuid)))
                               [icons/icon :check])]))
                        activities*))
                     tag-group-values)]))]])]))})))

(defn render
  [{:keys [close resource-name]} {curriculum-uuid :curriculum/uuid :as curriculum}]
  (let [no-selection
        "no-intro-video-selected"

        event-key
        (if (some? curriculum)
          ::>curricula/update-curriculum-v2
          ::>curricula/create-curriculum-v2)

        delete-event-key
        ::>curricula/delete-curriculum-v2

        is-submitting
        (re-frame/subscribe [:request/is-dispatching event-key])

        is-new
        (nil? curriculum)

        can-edit-advanced
        (re-frame/subscribe [:authentication/has-access?
                             :curriculum/can-edit-advanced
                             {:curriculum-uuid curriculum-uuid}])

        can-delete
        (re-frame/subscribe [:authentication/has-access?
                             :curriculum/can-delete
                             {:curriculum-uuid curriculum-uuid}])

        tag-groups
        (re-frame/subscribe [::kyber-db/get-model-records {:model :content-provider.tag-group}])]
    (fn [_ {curriculum-uuid :curriculum/uuid
            curriculum-name :curriculum/name
            :curriculum/keys [clp-enabled
                              compliance-enabled
                              pre-registration-enabled?
                              description
                              enforce-activity-order
                              participant-id-required
                              participant-id-label
                              max-number-of-activities
                              intro-activity
                              activities
                              valid-tags]
            :as curriculum
            :or {clp-enabled false
                 compliance-enabled false
                 pre-registration-enabled? true
                 enforce-activity-order false
                 participant-id-required false
                 participant-id-label nil}}]
      [:div {:class (:form-container classes)}
       [:div {:class (:left-section classes)}
        [rf/form {:id event-key
                  :is-submitting is-submitting}
         [:form
          {:rf/form
           {:on-submit
            #(let [{:curriculum/keys [intro-activity-uuid]} %]
               (re-frame/dispatch
                [event-key
                 (cond-> (dissoc % :curriculum/intro-activity-uuid)
                   (false? is-new)
                   (assoc :curriculum/uuid curriculum-uuid)

                   (not= intro-activity-uuid no-selection)
                   (assoc :curriculum/intro-activity {:activity/uuid intro-activity-uuid})

                             ;; The new activity check has to happen last in this cond->
                   (or (= intro-activity-uuid no-selection)
                       is-new)
                   (assoc :curriculum/intro-activity nil))
                 (cond-> {:response-keys [:curriculum/clp-enabled
                                          :curriculum/compliance-enabled
                                          :curriculum/description
                                          :curriculum/enforce-activity-order
                                          :curriculum/participant-id-required
                                          :curriculum/participant-id-label
                                          :curriculum/intro-activity
                                          :curriculum/leaderboard-enabled
                                          :curriculum/max-number-of-activities
                                          :curriculum/name
                                          :curriculum/pre-registration-enabled?
                                          :curriculum/self-reg-require-name
                                          :curriculum/uuid
                                          {:curriculum/valid-tags
                                           [:content-provider.tag-group/uuid
                                            :content-provider.tag-group/name
                                            {:content-provider.tag-group/values
                                             [:content-provider.tag/uuid
                                              :content-provider.tag/name]}]}]
                          :on-success-toast (if is-new
                                              "Curriculum created."
                                              "Curriculum updated.")
                          :on-failure-toast (if is-new
                                              "Something went wrong creating this curriculum."
                                              "Something went wrong updating this curriculum.")}
                   is-new
                   (assoc :on-success (fn [{curriculum-uuid :curriculum/uuid}]
                                        (re-frame/dispatch
                                         [:router/nav-to
                                          (admin-path :curriculum-activities
                                                      :curriculum-uuid curriculum-uuid)]))))]))}}
          [:div {:class (:form-section-container classes)}
           [:div
            (rf/input
             (cond-> {:field-key :curriculum/name
                      :id :name
                      :type :text
                      :label "Curriculum Title"
                      :validators [{:validator v/required
                                    :message "Required"}]
                      :transformers [t/trim]}
               (some? curriculum-name)
               (assoc :default-value curriculum-name)))

            (rf/textarea
             (cond-> {:field-key :curriculum/description
                      :id :description
                      :label "Short Description"
                      :validators [{:validator v/required
                                    :message "Required"}]
                      :transformers [t/trim]}
               (some? description)
               (assoc :default-value description)))

            (when-not is-new
              (rf/select-input
               {:field-key :curriculum/intro-activity-uuid
                :default-value (get intro-activity :activity/uuid  no-selection)
                :label "Introduction Activity"
                :options (into
                          [{:value no-selection
                            :display "No Intro Activity Selected"}]
                          (->> (if (some? intro-activity)
                                 (conj activities intro-activity)
                                 activities)
                               (filterv (fn [activity]
                                          (not (:activity/is-supplemental? activity))))
                               (mapv (fn [{activity-uuid :activity/uuid
                                           activity-name :activity/name}]
                                       {:value activity-uuid
                                        :display activity-name}))
                               (sort-by :display)))}))

            (when (feature-enabled? :curriculum-pre-reg)
              [:div {:class (:toggle-container classes)
                     :style {:margin-top (gs [:spacing :p20])}}
               [:div
                [text/caption-30 :label {}
                 "Controlled Registration"]
                [text/caption-20 :p {:style {:margin-right (gs [:spacing :p12])}}
                 (str "When enabled, Learners will not be able to register themselves, "
                      "but will need to be registered by an administrator in the "
                      "User Roster.")]]
               (rf/custom-field
                {:field-key :curriculum/pre-registration-enabled?
                 :id :pre-reg-enabled
                 :render toggle/render
                 :default-value (if (some? pre-registration-enabled?)
                                  pre-registration-enabled?
                                  is-new)})])

            [:div {:class (:toggle-container classes)
                   :style {:margin-top (gs [:spacing :p20])}}
             [:div
              [text/caption-30 :label {}
               "Require Name for Self-Registration"]
              [text/caption-20 :p {:style {:margin-right (gs [:spacing :p12])}}
               (str "When enabled, learners will be required to provide their first and last name "
                    "during self-registration. When disabled, learners will not be required to provide their name.")]]
             (rf/custom-field
              {:field-key :curriculum/self-reg-require-name
               :id :self-reg-require-name
               :render toggle/render
               :default-value (if (some? (:curriculum/self-reg-require-name curriculum))
                                (:curriculum/self-reg-require-name curriculum)
                                (boolean c/self-reg-require-name))})]

            [:div {:class (:toggle-container classes)}
             [:div
              [text/caption-30 :label {}
               "Curriculum Landing Page Enabled"]
              [text/caption-20 :p {}
               (str "When enabled, learners will be able to see all "
                    "activities in the curriculum.")]]
             (rf/custom-field
              {:field-key :curriculum/clp-enabled
               :id :clp-enabled
               :render clp-enabled-field/render
               :default-value clp-enabled})]

           ;; When there are tag-groups set on the content-provider, display them
           ;; at the curriculum level.
            (when (seq @tag-groups)
              (rf/custom-field
               {:field-key :curriculum/valid-tags
                :render tag-collections/render
                :default-value (tag-collections/tag-groups->tag-collection-value
                                {:available-tag-groups @tag-groups
                                 :selected-tag-groups valid-tags
                                 :allow-collection-toggling true
                                 :description-fn tag->tag-description
                                 :label-fn tag->tag-label})
                :on-change
                (fn [{:keys [value form-state toggle-action]}]
                  (if (false? (rfu/get-field-value @form-state :curriculum/clp-enabled))
                    (re-frame/dispatch
                     [:notifier/create-alert
                      {:title "This requires the Curriculum Landing Page to be enabled"
                       :message "Are you sure you want to enable the Curriculum Landing Page?"
                       :confirm-text "Enable"
                       :deny-text "Cancel"
                       :deny-action
                       #(reset! value false)
                       :confirm-action
                       #(do
                          (rfu/update-field-value! form-state :curriculum/clp-enabled true)
                          (toggle-action))}])
                    (toggle-action)))}))

            [:div {:class (:toggle-container classes)
                   :style {:margin-top (gs [:spacing :p20])}}
             [:div
              [text/caption-30 :label {} "Enforce Activity Order"]
              [text/caption-20 :p
               (str "When enabled, learners will have to complete "
                    "each activity in the order they are specified.")]]
             [:div {:class (:admin-input-container classes)}
              (rf/custom-field
               {:field-key :curriculum/enforce-activity-order
                :id :clp-enabled
                :render toggle/render
                :validators []
                :transformers []
                :default-value enforce-activity-order})]]

            [:div {:class (:toggle-container classes)
                   :style {:margin-top (gs [:spacing :p20])}}
             [:div
              [text/caption-30 :label {} "Require Participant ID"]
              [text/caption-20 :p
               "When enabled, learners will be required to enter a Participant ID during registration."]]
             [:div {:class (:admin-input-container classes)}
              (rf/custom-field
               {:field-key :curriculum/participant-id-required
                :id :participant-id-required
                :render toggle/render
                :default-value (boolean participant-id-required)})]]

            [:div {:style {:margin-top (gs [:spacing :p20])}}
             [:div
              [text/caption-30 :label
               "Participant ID Label"]
              [text/caption-20 :p
               {:style {:margin-bottom (str "-" (gs [:spacing :p12]))}}
               "Custom label for the Participant ID field. If left blank, defaults to \"Participant ID\"."]]
             [:div
              (rf/input
               {:field-key :curriculum/participant-id-label
                :id :participant-id-label
                :type :text
                :placeholder "Participant ID"
                :transformers [t/trim]
                :default-value participant-id-label})]]

            (when @can-edit-advanced
              [:<>
               [:div {:class (:admin-content-container classes)
                      :style {:margin-top (gs [:spacing :p20])}}
                [:div
                 [text/caption-30 :label
                  {:class (:admin-field-label classes)}
                  "Compliance Enabled"]
                 [text/caption-20 :p
                  (str "When enabled, this curriculum and all its activities will "
                       "be marked as compliant. This setting cannot be disabled once enabled.")]
                 (when compliance-enabled
                   [text/caption-30 :p
                    "✓ Compliance is currently enabled for this curriculum."])]
                [:div {:class (:admin-input-container classes)}
                 (rf/custom-field
                  {:field-key :curriculum/compliance-enabled
                   :id :compliance-enabled
                   :render toggle/render
                   :disabled compliance-enabled
                   :validators []
                   :transformers []
                   ;; cast to boolean because nil is valid
                   ;; but should be treated as false
                   ;; nil supplied to `custom-field` results in toggle "on"
                   :default-value (boolean compliance-enabled)})]]

               [:div {:class (:admin-content-container classes)
                      :style {:margin-top (gs [:spacing :p20])}}
                [:div
                 [text/caption-30 :label
                  {:class (:admin-field-label classes)}
                  "Leaderboard"]
                 [text/caption-20 :p
                  (str "The leaderboard shows how learners compare to their "
                       "peers. It displays on the CLP to the right of the "
                       "activity list.")]
                 [text/caption-30 :p
                  (str "Note: This feature is temporarily disabled.")]]
                [:div {:class (:admin-input-container classes)}
                 (rf/custom-field
                  {:field-key :curriculum/leaderboard-enabled
                   :id :leaderboard-enabled
                   :render toggle/render
                   :disabled true
                   :validators []
                   :transformers []
                   :default-value false})]]

               [:div {:class (:admin-content-container classes)}
                [:div
                 [text/caption-30 :label
                  {:class (:admin-field-label classes)}
                  "Maximum Number of Activities"]
                 [text/caption-20 :p
                  (str "Enter the number of activities that can be created "
                       "within this curriculum")]]
                [:div {:class (:admin-input-container classes)}
                 (rf/input
                  (cond-> {:field-key :curriculum/max-number-of-activities
                           :id :max-number-of-activities
                           :type :number
                           :validators [{:validator v/required
                                         :message "Required"}]
                           :transformers [t/trim
                                          t/str->int]}
                    (some? max-number-of-activities)
                    (assoc :default-value (str max-number-of-activities))))]]])]]
          (crud-container/ctas
           (cond-> {:close close
                    :resource-name resource-name
                    :is-new is-new
                    :cancel-button-copy (if is-new "Cancel" "Close")}
             (and (some? curriculum)
                  @can-delete)
             (assoc :on-delete
                    (fn []
                      (re-frame/dispatch
                       [:notifier/create-alert
                        {:title "Remove curriculum?"
                         :message "Are you sure you want to remove this curriculum?"
                         :confirm-text "Remove curriculum"
                         :deny-text "Cancel"
                         :confirm-action
                         (fn []
                           (re-frame/dispatch
                            [delete-event-key
                             {:curriculum/uuid curriculum-uuid}
                             {:on-success-toast "Curriculum removed."
                              :on-success #(re-frame/dispatch
                                            [:router/nav-to "/admin"])}]))}])))))]]]
       [:div {:class (:right-section classes)}
        (when-not is-new
          [render-clp-poster-uploader curriculum])

        (when (and @can-edit-advanced
                   (not is-new))
          [render-curriculum-logo-uploader curriculum])

        (when (seq activities)
          (let [curriculum-valid-tags
                (filter #(seq (:content-provider.tag-group/values %))
                        valid-tags)]
            (when (seq curriculum-valid-tags)
              [:<>
               [text/caption-30 :p {:style {:margin-top (gs [:spacing :p40])}}
                "Visibility of activities in this curriculum"]
               [tag-group-matrix
                {:curriculum-uuid curriculum-uuid
                 :curriculum-valid-tags curriculum-valid-tags
                 :activities activities}]])))]])))
